import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

// The In-Match card's character picker (Tekken 8): a field showing the pick,
// which opens a grid of the roster, four across and five rows tall before it
// scrolls, each character as the game's own select-screen thumbnail
// (public/overlay/images/tekken-8/characters/thumb/<slug>.webp, pulled by
// scripts/fetch-tekken8-thumbs.mjs). A native <select> can't show pictures.
//
// Keyboard, as a listbox: the arrows move through the grid (up and down by a
// row), Home / End, Page Up / Page Down by five rows, Enter or Space picks,
// Escape closes, and typing a name's first letters jumps to it.
//
// `outlined` is the set of characters the In-Match labels can cut an outline
// for (hud/index.json); the others are marked, since their label stays whole.

const THUMBS = '/overlay/images/tekken-8/characters/thumb';
const COLUMNS = 4;
const ROWS = 5;
const TYPEAHEAD_MS = 700;
const NONE = ['', 'None'];

const initials = (name) => name.split(/[\s-]+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

export default function CharacterPicker({
  id, label, value, options, outlined, disabled, align = 'start', onChange,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState(() => new Set());
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const gridRef = useRef(null);
  const cellRefs = useRef([]);
  const typed = useRef({ text: '', at: 0 });
  const listId = useId();

  const items = [NONE, ...options];
  const picked = value ?? '';
  const name = picked ? new Map(options).get(picked) ?? picked : null;
  const unoutlined = (slug) => !!slug && !!outlined && !outlined.has(slug);
  const anyUnoutlined = options.some(([slug]) => unoutlined(slug));

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // Five rows tall at whatever width the cells come out, the pick in the
  // middle of the view, and the focus on it.
  useLayoutEffect(() => {
    if (!open) return;
    const grid = gridRef.current;
    const first = cellRefs.current[0];
    const cell = cellRefs.current[active];
    if (!grid || !first) return;
    const css = getComputedStyle(grid);
    const gap = parseFloat(css.rowGap) || 0;
    const pad = (parseFloat(css.paddingTop) || 0) + (parseFloat(css.paddingBottom) || 0);
    grid.style.maxHeight = `${ROWS * first.offsetHeight + (ROWS - 1) * gap + pad}px`;
    if (cell) {
      grid.scrollTop = cell.offsetTop - (grid.clientHeight - cell.offsetHeight) / 2;
      cell.focus({ preventScroll: true });
    }
    // Only on opening: moving through the grid scrolls by itself (move).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openGrid = () => {
    if (disabled) return;
    setActive(Math.max(0, items.findIndex(([slug]) => slug === picked)));
    setOpen(true);
  };

  const close = (refocus) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const choose = (slug) => {
    if (slug !== picked) onChange(slug);
    close(true);
  };

  const move = (to) => {
    const next = Math.max(0, Math.min(items.length - 1, to));
    setActive(next);
    const cell = cellRefs.current[next];
    if (!cell) return;
    cell.focus({ preventScroll: true });
    cell.scrollIntoView({ block: 'nearest' });
  };

  // One letter again goes on to the next name with it; letters typed
  // together spell the start of a name.
  const typeahead = (ch) => {
    const now = Date.now();
    const t = typed.current;
    t.text = now - t.at > TYPEAHEAD_MS ? ch : t.text + ch;
    t.at = now;
    const repeat = t.text.length > 1 && [...t.text].every((c) => c === t.text[0]);
    const text = repeat ? t.text[0] : t.text;
    const from = text.length === 1 ? active + 1 : active;
    for (let n = 0; n < items.length; n++) {
      const i = (from + n) % items.length;
      if (items[i][0] && items[i][1].toLowerCase().startsWith(text)) { move(i); return; }
    }
  };

  const onButtonKey = (e) => {
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      // Not the button's own click: it would land on the grid's first focus.
      e.preventDefault();
      if (open) close(false);
      else openGrid();
    }
  };

  const onGridKey = (e) => {
    const { key } = e;
    if (key === 'ArrowRight') move(active + 1);
    else if (key === 'ArrowLeft') move(active - 1);
    else if (key === 'ArrowDown') { if (active + COLUMNS < items.length) move(active + COLUMNS); }
    else if (key === 'ArrowUp') { if (active - COLUMNS >= 0) move(active - COLUMNS); }
    else if (key === 'Home') move(0);
    else if (key === 'End') move(items.length - 1);
    else if (key === 'PageDown') move(active + COLUMNS * ROWS);
    else if (key === 'PageUp') move(active - COLUMNS * ROWS);
    else if (key === 'Enter' || key === ' ') choose(items[active][0]);
    else if (key === 'Escape') close(true);
    else if (key.length === 1 && key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) typeahead(key.toLowerCase());
    else return;
    e.preventDefault();
  };

  // Focus leaving the picker (Tab past the grid) closes it.
  const onMenuBlur = (e) => {
    if (!rootRef.current?.contains(e.relatedTarget)) setOpen(false);
  };

  const art = (slug, title) => {
    if (!slug) return <span className="char-picker__art char-picker__art--none" aria-hidden="true">—</span>;
    return (
      <span className="char-picker__art">
        {broken.has(slug)
          ? <span className="char-picker__initials" aria-hidden="true">{initials(title)}</span>
          : (
            <img
              src={`${THUMBS}/${slug}.webp`}
              alt=""
              decoding="async"
              draggable={false}
              onError={() => setBroken((prev) => new Set(prev).add(slug))}
            />
          )}
        {unoutlined(slug) && <span className="char-picker__flag" aria-hidden="true" />}
      </span>
    );
  };

  return (
    <div className="char-picker" ref={rootRef}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className="form-select form-select-sm char-picker__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        onClick={() => (open ? close(false) : openGrid())}
        onKeyDown={onButtonKey}
      >
        {picked ? (
          <>
            {art(picked, name)}
            <span className="char-picker__value">{name}</span>
            {unoutlined(picked) && <span className="char-picker__note">no outline yet</span>}
          </>
        ) : (
          <span className="char-picker__value char-picker__value--empty">—</span>
        )}
      </button>

      {open && (
        // Focusable throughout (tabIndex -1), so a click on a gap or on the
        // legend keeps the focus inside and the grid open.
        <div
          className={`char-picker__menu char-picker__menu--${align}`}
          tabIndex={-1}
          onBlur={onMenuBlur}
          onKeyDown={onGridKey}
        >
          <div
            ref={gridRef}
            id={listId}
            className="char-picker__grid"
            role="listbox"
            aria-label={label}
            tabIndex={-1}
          >
            {items.map(([slug, title], i) => {
              const selected = slug === picked;
              const tip = unoutlined(slug) ? `${title} (no outline yet)` : title;
              return (
                <button
                  key={slug || 'none'}
                  ref={(el) => { cellRefs.current[i] = el; }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-label={tip}
                  title={tip}
                  tabIndex={i === active ? 0 : -1}
                  className={`char-picker__cell${selected ? ' is-selected' : ''}`}
                  onClick={() => choose(slug)}
                  onFocus={() => setActive(i)}
                >
                  {art(slug, title)}
                  <span className="char-picker__name">{title}</span>
                </button>
              );
            })}
          </div>
          {anyUnoutlined && (
            <p className="char-picker__legend">
              <span className="char-picker__flag char-picker__flag--inline" aria-hidden="true" />
              No outline yet: the label stays whole.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
